import { useMemo, useState } from "react";
import { BookmarkX, Quote, Search } from "lucide-react";
import type { Asset, ReferenceItem } from "../../api/types.js";
import { isSafeHref } from "../shared/safeHref.js";
import { CitationDialog } from "../works/CitationDialog.js";
import { workDetailUrl } from "../works/workApi.js";

function authorNames(authors: Array<string | Record<string, any>> = []) {
  return authors.map((author) => typeof author === "string" ? author : author.name || [author.givenName, author.familyName].filter(Boolean).join(" ")).filter(Boolean);
}

export function ConversationAssetsTab({
  assets,
  currentSessionId,
  saveSourceAsAsset
}: {
  assets: Asset[];
  currentSessionId?: string | null;
  saveSourceAsAsset: (source: ReferenceItem) => void | Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [citationAsset, setCitationAsset] = useState<Asset | null>(null);
  const [actionError, setActionError] = useState("");
  const filtered = useMemo(() => assets.filter((asset) => asset.title.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())), [assets, query]);

  function favoriteSource(asset: Asset): ReferenceItem {
    return {
      workId: asset.workId,
      poolId: asset.workId || asset.id,
      title: asset.title,
      authors: authorNames(asset.meta?.authors),
      year: asset.meta?.year,
      venue: asset.meta?.venue,
      url: asset.url,
      isSaved: true
    };
  }

  async function removeFavorite(asset: Asset) {
    setActionError("");
    try {
      await saveSourceAsAsset(favoriteSource(asset));
    } catch {
      setActionError("取消收藏失败，请重试。");
    }
  }

  return (
    <div className="conversation-assets-panel">
      <div className="conversation-assets-toolbar">
        <label className="conversation-assets-search">
          <Search size={14} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索对话中的资料" />
        </label>
      </div>
      {assets.length === 0 && <p className="empty-note">还没有收藏或添加到当前对话的资料。</p>}
      {assets.length > 0 && filtered.length === 0 && <p className="empty-note">没有匹配的资料。</p>}
      {actionError && <p className="asset-action-error" role="alert">{actionError}</p>}
      {filtered.map((asset) => {
        const detailUrl = asset.workId ? workDetailUrl(asset.workId, currentSessionId) : "";
        return (
          <article className="asset-card" key={asset.id}>
            {detailUrl ? <a className="asset-title" href={detailUrl} target="_blank" rel="noreferrer">{asset.title}</a> : <strong>{asset.title}</strong>}
            <span>{asset.meta?.venue || asset.kind}{asset.meta?.year ? ` · ${asset.meta.year}` : asset.mimeType ? ` · ${asset.mimeType}` : ""}</span>
            {asset.workId ? (
              <div className="asset-actions">
                <button type="button" onClick={() => setCitationAsset(asset)}><Quote size={12} /> 引用</button>
                <button type="button" onClick={() => void removeFavorite(asset)}><BookmarkX size={12} /> 取消收藏</button>
              </div>
            ) : asset.url && isSafeHref(asset.url) ? <a href={asset.url} target="_blank" rel="noreferrer">打开资料</a> : null}
          </article>
        );
      })}
      {citationAsset?.workId && <CitationDialog workId={citationAsset.workId} title={citationAsset.title} open onClose={() => setCitationAsset(null)} />}
    </div>
  );
}
